import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from 'react-hot-toast';
import {
  useTheme,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  EditOutlined,
  DeleteOutlined,
  Visibility,
  VisibilityOff,
  ArrowBackIosNew,
  Fingerprint,
} from "@mui/icons-material";
import { startAuthentication } from '@simplewebauthn/browser';
import { Formik } from "formik";
import * as yup from "yup";
import { setLogin, setCrewRanks, setToken } from "state";
import { API_URL } from 'state/constants';
import api from 'state/axios';
import { fetchParams, fetchCrewRanks } from 'state/api';
import { initSocket } from "state/socket";
import { logClientAction } from 'state/logger';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import Dropzone from "react-dropzone";
import FlexBetween from "components/FlexBetween";


/* ===== PROVIDER LOGOS ===== */
const MicrosoftLogo = () => (
  <svg width="18" height="18" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg">
    <path fill="#f35325" d="M1 1h10v10H1z"/>
    <path fill="#81bc06" d="M12 1h10v10H12z"/>
    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
    <path fill="#ffba08" d="M12 12h10v10H12z"/>
  </svg>
);

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/>
    <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"/>
  </svg>
);

/* ===== MODULE-LEVEL CONSTANTS ===== */
const PROVIDERS = [
  {
    id: 'outlook',
    name: 'Sign in with Microsoft',
    icon: <MicrosoftLogo />,
    disabled: false,
  },
  {
    id: 'google',
    name: 'Sign in with Google',
    icon: <GoogleLogo />,
    disabled: false,
  },
];

const WEBAUTHN_SUPPORTED = typeof window !== 'undefined' && !!window.PublicKeyCredential;

// Keys must exactly match pageType state values
const validationSchemas = {
  register: yup.object({
    firstName: yup.string().required("required"),
    lastName:  yup.string().required("required"),
    email:     yup.string().email("invalid email").required("required"),
    password:  yup.string().required("required"),
  }),
  login: yup.object({
    email:    yup.string().email("invalid email").required("required"),
    password: yup.string().required("required"),
  }),
  forgotpassword: yup.object({
    email: yup.string().email("Invalid email").required("Required"),
  }),
  resetpassword: yup.object({
    password:        yup.string().required("Required"),
    confirmPassword: yup.string()
      .oneOf([yup.ref('password')], "Passwords must match")
      .required("Required"),
  }),
};

const SUBMIT_LABELS = {
  login:          "Login",
  register:       "Register",
  forgotpassword: "Submit",
  resetpassword:  "Reset",
};

const initialValues = {
  register:       { firstName: "", lastName: "", location: "", occupation: "", picture: "", email: "", password: "", role: "guest" },
  login:          { email: "", password: "" },
  forgotpassword: { email: "" },
  resetpassword:  { password: "", confirmPassword: "" },
};


/* ===== DROPZONE ===== */
const DropzoneComponent = ({ image, setImage }) => {
  const { palette } = useTheme();

  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Only JPG/PNG allowed.");
      return;
    }
    setImage(file);
  };

  return (
    <Dropzone acceptedFiles=".jpg,.jpeg,.png" multiple={false} onDrop={handleDrop}>
      {({ getRootProps, getInputProps }) => (
        <FlexBetween>
          <Box
            {...getRootProps()}
            width="100%"
            border={`2px dashed ${palette.primary.main}`}
            p="1rem"
            sx={{ "&:hover": { cursor: "pointer" } }}
          >
            <input {...getInputProps()} />
            {!image ? (
              <p>Add Image Here</p>
            ) : (
              <FlexBetween>
                <Typography>{image.name}</Typography>
                <EditOutlined />
              </FlexBetween>
            )}
          </Box>
          {image && (
            <IconButton 
              onClick={() => setImage(null)}
              sx={{ width: "15%" }} 
            >
              <DeleteOutlined />
            </IconButton>
          )}
        </FlexBetween>
      )}
    </Dropzone>
  );
};

/* ======= FORM ======= */
const Form = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { palette } = useTheme();
  const location = useLocation();

  const { setStartDate, setEndDate } = useDateRangeContext();

  const [pageType, setPageType]       = useState("login");
  const [image, setImage]             = useState(null);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [sentEmail, setSentEmail] = useState(null);
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false,
  });

  const submitLabel = useMemo(() => SUBMIT_LABELS[pageType] ?? "Submit", [pageType]);

  /* ---- Helpers ---- */
  const applyPreferences = useCallback((preferences) => {
    if (preferences?.startDate) setStartDate(new Date(preferences.startDate));
    if (preferences?.endDate)   setEndDate(new Date(preferences.endDate));
  }, [setStartDate, setEndDate]);

  const finalizeLogin = useCallback(async (token, user) => {
    dispatch(setToken(token));
    const [params, crewRanks] = await Promise.all([
      fetchParams(),
      fetchCrewRanks(),
    ]);
    dispatch(setLogin({ user, token, params }));
    dispatch(setCrewRanks({ crewRanks }));
    initSocket(user._id || null);
    navigate("/main");
    logClientAction("login", user.email || '');
  }, [dispatch, navigate]);

  /* ---- URL param handling (OAuth redirect + password-reset links) ---- */
  const handleOAuthLogin = useCallback(async (token, error, message) => {
    if (error) {
      toast.error(decodeURIComponent(error));
      navigate("/auth/login", { replace: true });
      return;
    }
    if (!token) return;

    try {
      dispatch(setToken(token));
      const { data } = await api.get('/auth/user');
      
      applyPreferences(data.user?.preferences);
      await finalizeLogin(token, data.user);

      if (message) toast(decodeURIComponent(message), { duration: 8000 });
    } catch (err) {
      dispatch(setToken(null));
      toast.error(err?.response?.data?.message || err?.message || "Failed to process OAuth2 login!");
      navigate("/auth/login", { replace: true });
    }
  }, [applyPreferences, finalizeLogin, dispatch, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location?.search);
    const token   = params.get("token");
    const error   = params.get("error");
    const rt      = params.get("resetToken");
    const message = params.get("message");

    if (rt) {
      setResetToken(rt);
      setPageType("resetpassword");
      return;
    }

    if (token || error) {
      handleOAuthLogin(token, error, message).catch(() => {
        navigate("/auth/login", { replace: true });
      });
    }
  }, [location.search, handleOAuthLogin, navigate]);

  /* ---- Biometric login ---- */
  const handleBiometricLogin = useCallback(async (email) => {
    if (!email) {
      toast.error("Enter email to use biometrics.");
      return;
    }
    try {
      setBiometricLoading(true);
      const { data: options } = await api.post('/auth/webauthn/authentication-options', { email });
      const response = await startAuthentication({ optionsJSON: options });
      const { data } = await api.post('/auth/webauthn/authentication-verify', { email, response });
      if (data.success) {
        applyPreferences(data.user?.preferences);
        await finalizeLogin(data.token, data.user);
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error("Biometric authentication was not available.");
      } else {
        toast.error(err.response?.data?.message || err.message || "Biometric login failed!");
      }
    } finally {
      setBiometricLoading(false);
    }
  }, [applyPreferences, finalizeLogin]);

  /* ---- OAuth sign-in ---- */
  const signIn = useCallback(async (provider) => {
    try {
      window.location.href = `${API_URL}/auth/${provider}`;
    } catch (error) {
      toast.error(`Failed to sign in with ${provider}: ${error.message}`);
    }
  }, []);

  /* ---- Password visibility toggle ---- */
  const togglePasswordVisibility = useCallback((key) => {
    setShowPasswords((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  /* ---- Form submit ---- */
  const handleFormSubmit = useCallback(async (values, { resetForm }) => {
    try {
      if (pageType === "register") {
        const formData = new FormData();
        Object.entries(values).forEach(([k, v]) => formData.append(k, v));
        if (image) {
          formData.append("picture", image);
          formData.append("picturePath", image.name);
        }
        const { data } = await api.post(`/auth/register`, formData);
        if (data.success) {
          setImage(null);
          setPageType("login");
          toast.success("Registration successful. Please login.");
        }

      } else if (pageType === "login") {
        const { data } = await api.post('/auth/login', values);
        if (data.success) {
          applyPreferences(data.user?.preferences);
          await finalizeLogin(data.token, data.user);
        }

      } else if (pageType === "forgotpassword") {
        const { data } = await api.post(`/auth/forgotpassword`, values);
        if (data.success) {
          setSentEmail(values.email);
        }

      } else if (pageType === "resetpassword") {
        const { data } = await api.post(`/auth/resetpassword/${resetToken}`, values);
        if (data.success) {
          setResetToken("");
          setPageType("login");
          toast.success("Password has been reset.");
        } else {
          toast.error("Failed to reset password!");
        }
      }

      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "An error occurred. Please try again.");
    }
  }, [
    pageType, 
    image, 
    resetToken, 
    applyPreferences, 
    finalizeLogin,
  ]);


  return (
    <>
      {/* ===== HEADER ROW ===== */}
      <Box display="flex" alignItems="center" mb={3}>
        <Box sx={{ width: 34 }}>
          {pageType !== "login" && pageType !== "register" && (
            <IconButton
              size="small"
              onClick={() => { setPageType("login"); setSentEmail(null); }}
              sx={{ 
                color: palette.text.secondary, 
                "&:hover": { color: palette.primary.main } 
              }}
            >
              <ArrowBackIosNew fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Typography 
          flex={1} 
          variant="h5" 
          textAlign="center" 
          color="primary"
        >
          DIGITAIR
        </Typography>
        <Box sx={{ width: 34 }} />
      </Box>

      {/* Email sent confirmation */}
      {pageType === "forgotpassword" && sentEmail && (
        <Box textAlign="center" py={2}>
          <Typography variant="h6" color="primary" mb={1}>
            Check your email
          </Typography>
          <Typography color="text.secondary" mb={2}>
            We sent a password reset link to <strong>{sentEmail}</strong>.
            The link expires in 1 hour.
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Didn't receive it? Check your spam folder or{" "}
            <Typography
              component="span"
              variant="body2"
              onClick={() => setSentEmail(null)}
              sx={{ 
                color: "primary.main", 
                cursor: "pointer", 
                textDecoration: "underline",
              }}
            >
              try again
            </Typography>.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => { 
              setPageType("login"); 
              setSentEmail(null); 
            }}
            sx={{ textTransform: "none" }}
          >
            Back to Login
          </Button>
        </Box>
      )}

      {!(pageType === "forgotpassword" && sentEmail) && (
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues[pageType]}
        validationSchema={validationSchemas[pageType]}
        enableReinitialize
      >
        {({ 
          values, 
          errors, 
          touched, 
          handleBlur, 
          handleChange, 
          handleSubmit, 
          resetForm 
        }) => {
          const field = (name) => ({
            name,
            onBlur: handleBlur,
            onChange: handleChange,
            value: values[name] ?? "",
            error: Boolean(touched[name] && errors[name]),
            helperText: touched[name] && errors[name],
          });

          return (
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                gap={2}
                sx={{
                  "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
                    WebkitBoxShadow: `0 0 0 1000px ${palette.background.alt} inset`,
                    WebkitTextFillColor: palette.text.primary,
                  },
                }}
              >
                {/* Register-only fields */}
                {pageType === "register" && (
                  <React.Fragment>
                    <TextField {...field("firstName")}  label="First Name" aria-label="First Name" sx={{ gridColumn: "span 6" }} />
                    <TextField {...field("lastName")}   label="Last Name"  aria-label="Last Name"  sx={{ gridColumn: "span 6" }} />
                    <TextField {...field("location")}   label="Location"   aria-label="Location"   sx={{ gridColumn: "span 12" }} />
                    <TextField {...field("occupation")} label="Occupation" aria-label="Occupation" sx={{ gridColumn: "span 12" }} />
                    <Box 
                      gridColumn="span 12" 
                      borderRadius={1} 
                      border={`1px solid ${palette.neutral.deep}`} 
                      p={1}
                    >
                      <DropzoneComponent image={image} setImage={setImage} />
                    </Box>
                  </React.Fragment>
                )}

                {/* Email — shown on all page types except resetpassword */}
                {pageType !== "resetpassword" && (
                  <TextField 
                    {...field("email")} 
                    label="Email" 
                    aria-label="Email" 
                    sx={{ gridColumn: "span 12" }} 
                  />
                )}

                {/* Password — not shown on forgotpassword */}
                {pageType !== "forgotpassword" && (
                  <TextField
                    {...field("password")}
                    label="Password"
                    aria-label="Password"
                    type={showPasswords.password ? "text" : "password"}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              disableRipple
                              onClick={() => togglePasswordVisibility("password")}
                              sx={{ 
                                bgcolor: 'transparent', 
                                '&:hover': { bgcolor: 'transparent' } 
                              }}
                            >
                              {showPasswords.password ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ gridColumn: "span 12" }}
                  />
                )}

                {/* Confirm password — resetpassword only */}
                {pageType === "resetpassword" && (
                  <TextField
                    {...field("confirmPassword")}
                    label="Confirm New Password"
                    aria-label="Confirm New Password"
                    type={showPasswords.confirmPassword ? "text" : "password"}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              disableRipple
                              onClick={() => togglePasswordVisibility("confirmPassword")}
                              sx={{ 
                                bgcolor: 'transparent', 
                                '&:hover': { bgcolor: 'transparent' } 
                              }}
                            >
                              {showPasswords.confirmPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ gridColumn: "span 12" }}
                  />
                )}
              </Box>

              {/* Submit button row */}
              <Box 
                display="flex" 
                alignItems="stretch" 
                gap={1} 
                sx={{ m: "1rem 0" }}
              >
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  sx={{
                    p: "1rem",
                    bgcolor: palette.primary.main,
                    color: palette.getContrastText(palette.primary.main),
                    "&:hover": {
                      bgcolor: palette.primary.dark,
                      color: palette.getContrastText(palette.primary.dark),
                    },
                  }}
                >
                  {submitLabel}
                </Button>
                {pageType === "login" && WEBAUTHN_SUPPORTED && (
                  <Button
                    onClick={() => handleBiometricLogin(values.email)}
                    disabled={biometricLoading}
                    sx={{
                      borderRadius: 1,
                      px: 2.5,
                      textTransform: "none",
                      color: palette.text.primary,
                      border: "1px solid",
                      borderColor: palette.neutral?.deep ?? palette.divider,
                      "&:hover": { borderColor: palette.primary.main },
                    }}
                  >
                    {biometricLoading
                      ? <CircularProgress size={22} />
                      : <Fingerprint />
                    }
                  </Button>
                )}
              </Box>

              {/* Navigation links */}
              <FlexBetween>
                <Typography
                  onClick={() => {
                    setPageType(pageType === "login" ? "register" : "login");
                    resetForm();
                  }}
                  sx={{
                    textDecoration: "underline",
                    color: palette.text.disabled,
                    pointerEvents: "none",
                    "&:hover": { cursor: "pointer", color: palette.neutral.main },
                  }}
                >
                  {pageType === "login" ? "Don't have an account? Sign up" : "Already have an account? Login"}
                </Typography>

                {pageType === "login" && (
                  <Typography
                    onClick={() => { setPageType("forgotpassword"); resetForm(); }}
                    sx={{
                      textDecoration: "underline",
                      color: palette.primary.main,
                      cursor: "pointer",
                      "&:hover": { color: palette.neutral.main },
                    }}
                  >
                    Forgot password
                  </Typography>
                )}

              </FlexBetween>

              {/* OAuth providers */}
              {pageType === "login" && (
                <Box mt={2}>
                  <Box display="flex" alignItems="center" gap={2} mb={1.5}>
                    <Box 
                      flex={1} 
                      height="1px" 
                      bgcolor={palette.neutral?.deep ?? palette.divider} 
                    />
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      or
                    </Typography>
                    <Box 
                      flex={1} 
                      height="1px" 
                      bgcolor={palette.neutral?.deep ?? palette.divider} 
                    />
                  </Box>
                  <Stack spacing={2}>
                    {PROVIDERS.map((provider) => (
                      <Button
                        key={provider.id}
                        fullWidth
                        variant="outlined"
                        onClick={() => signIn(provider.id)}
                        disabled={provider.disabled}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          textTransform: "none",
                          color: palette.text.primary,
                          borderColor: palette.neutral?.deep ?? palette.divider,
                          "&:hover": { borderColor: palette.primary.main },
                          gap: 2,
                          p: 1,
                        }}
                      >
                        {provider.icon}
                        <Typography variant="body1" component="span">
                          {provider.name}
                        </Typography>
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}
            </form>
          );
        }}
      </Formik>
      )}
    </>
  );
};

export default Form;
